
import React from 'react';
import { Calendar, Video, Phone, PlusCircle } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Patient } from '@/types/doctor';

interface PatientAppointmentsProps {
  patient: Patient & {
    appointments?: {
      id: string;
      date: string;
      time: string;
      type: string;
      status: 'completed' | 'scheduled' | 'cancelled';
      notes?: string;
    }[];
  };
}

const PatientAppointments: React.FC<PatientAppointmentsProps> = ({ patient }) => {
  const appointments = patient.appointments || [];
  
  // Function to get type icon
  const getTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'video':
        return <Video className="h-4 w-4 text-blue-500" />;
      case 'voice':
        return <Phone className="h-4 w-4 text-purple-500" />;
      default:
        return <Calendar className="h-4 w-4 text-green-500" />;
    }
  };
  
  // Function to get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">Completed</Badge>;
      case 'scheduled':
        return <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">Scheduled</Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Appointments ({appointments.length})</h3>
        <Button>
          <PlusCircle className="h-4 w-4 mr-2" />
          Schedule New Appointment
        </Button>
      </div>
      
      {appointments.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {appointments.map((appointment) => (
                  <TableRow key={appointment.id}>
                    <TableCell>
                      <div className="font-medium">
                        {new Date(appointment.date).toLocaleDateString()}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {appointment.time}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getTypeIcon(appointment.type)}
                        <span>{appointment.type}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(appointment.status)}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {appointment.notes || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <div className="p-8 text-center border rounded-md bg-muted/10">
          <p className="text-muted-foreground mb-4">No appointments scheduled yet</p>
          <Button>
            <PlusCircle className="h-4 w-4 mr-2" />
            Schedule First Appointment
          </Button>
        </div>
      )}
    </div>
  );
};

export default PatientAppointments;
